import React, { useRef, useState, useLayoutEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function DemoUserTooltip({ userData, position, visible }) {
  const tooltipRef = useRef(null);
  const [adjustedPosition, setAdjustedPosition] = useState({ x: 0, y: 0 });

  useLayoutEffect(() => {
    if (!visible || !tooltipRef.current) {
      return;
    }

    const tooltip = tooltipRef.current;
    const rect = tooltip.getBoundingClientRect();
    const tooltipWidth = rect.width || 300;
    const tooltipHeight = rect.height || 400;
    const padding = 20;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let x = position.x;
    let y = position.y;

    if (x + tooltipWidth + padding > viewportWidth) {
      x = position.x - tooltipWidth - 80;
    }

    if (x < padding) {
      x = padding;
    }

    if (y + tooltipHeight + padding > viewportHeight) {
      y = viewportHeight - tooltipHeight - padding;
    }

    if (y < padding) {
      y = padding;
    }

    setAdjustedPosition({ x, y });
  }, [position, visible]);

  if (!userData || !visible) return null;

  const { demographics, health, lifestyle, dataPoints, matchScore } = userData;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          ref={tooltipRef}
          className="demo-user-tooltip"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.15 }}
          style={{
            position: "fixed",
            left: adjustedPosition.x || position.x,
            top: adjustedPosition.y || position.y,
            zIndex: 1000,
          }}
        >
          <div className="tooltip-header">
            <span className="anonymous-badge">Anonymous User</span>
            <span className="match-score">{matchScore}% Match</span>
          </div>

          <div className="tooltip-section">
            <div className="section-title">Demographics</div>
            <div className="info-row">
              <span className="label">Gender:</span>
              <span className="value">{demographics.gender}</span>
            </div>
            <div className="info-row">
              <span className="label">Age Range:</span>
              <span className="value">{demographics.ageRange}</span>
            </div>
            <div className="info-row">
              <span className="label">Region:</span>
              <span className="value">{demographics.region}</span>
            </div>
          </div>

          <div className="tooltip-section">
            <div className="section-title">Health Profile</div>
            <div className="info-row">
              <span className="label">Blood Type:</span>
              <span className="value">{health.bloodType}</span>
            </div>
            <div className="info-row">
              <span className="label">Surgery History:</span>
              <span className="value">{health.hasSurgeryHistory ? "Yes" : "No"}</span>
            </div>
            {health.hasSurgeryHistory && health.surgeryTypes.length > 0 && (
              <div className="info-row">
                <span className="label">Procedures:</span>
                <span className="value">{health.surgeryTypes.join(", ")}</span>
              </div>
            )}
            {health.chronicConditions.length > 0 && (
              <div className="info-row">
                <span className="label">Conditions:</span>
                <span className="value">{health.chronicConditions.join(", ")}</span>
              </div>
            )}
            <div className="info-row">
              <span className="label">Fitness Level:</span>
              <span className="value">{health.fitnessLevel}</span>
            </div>
          </div>

          <div className="tooltip-section">
            <div className="section-title">Lifestyle</div>
            <div className="info-row">
              <span className="label">Diet:</span>
              <span className="value">{lifestyle.dietPreference}</span>
            </div>
            <div className="info-row">
              <span className="label">Exercise:</span>
              <span className="value">{lifestyle.exerciseFrequency}</span>
            </div>
            <div className="info-row">
              <span className="label">Sleep Quality:</span>
              <span className="value">{lifestyle.sleepQuality}</span>
            </div>
          </div>

          <div className="tooltip-footer">
            <span className="data-points">{dataPoints.toLocaleString()} data points shared</span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
